// The one HTTP client (RC1): the only `fetch` in the SPA. Generated hooks call it as their Orval
// mutator, so every request has the same origin check, headers and error mapping.
import { ApiError, TransportError, parseErrorEnvelope } from "./errors";
import { MEDIA_PREFERENCE, prefersBareMedia } from "./media-cookie";

/** Orval types every generated hook's error with this. */
export type ErrorType<_Body> = ApiError | TransportError;

const API_PREFIX = "/api/";

/**
 * Resolves a path against this page's origin and refuses anything else: the SPA and the API share
 * one origin (proposal 6.1), so a URL elsewhere is a bug, never a feature.
 */
export function resolveApiUrl(path: string): URL {
  const origin = window.location.origin;
  const url = new URL(path, origin);
  if (url.origin !== origin || !url.pathname.startsWith(API_PREFIX)) {
    throw new Error(`apiRequest only calls same-origin ${API_PREFIX} paths, not ${path}`);
  }
  return url;
}

// Checked by name: the DOMException class differs between realms (a test's jsdom and Node).
function isAbort(error: unknown): boolean {
  return (
    typeof error === "object" && error !== null && "name" in error && error.name === "AbortError"
  );
}

/**
 * Sends one API request. Resolves with the parsed JSON body (`undefined` for an empty one) and
 * rejects with ApiError or TransportError. An abort rejects with the AbortError unchanged.
 */
export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = resolveApiUrl(path);
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  // A team's media URLs come back as each file's own while this tab holds its media cookie.
  if (prefersBareMedia(url.pathname)) headers.set("Prefer", MEDIA_PREFERENCE);

  let status: number;
  let ok: boolean;
  let text: string;
  try {
    const response = await fetch(url, { ...init, headers, credentials: "same-origin" });
    status = response.status;
    ok = response.ok;
    text = await response.text();
  } catch (error) {
    if (init.signal?.aborted === true || isAbort(error)) throw error;
    throw new TransportError("network", undefined, { cause: error });
  }

  if (!ok) {
    const body = parseErrorEnvelope(text);
    if (body !== undefined) throw new ApiError(status, body);
    throw new TransportError("http", status);
  }
  if (text === "") return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch (error) {
    throw new TransportError("malformed", status, { cause: error });
  }
}
